import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaBolt,
  FaEnvelope,
  FaFacebook,
  FaGlobe,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import useAxios from '../../api/axios';
import { ACTIVITY_ID } from '../../constants/activity';
import { useAuth } from '../../context/AuthProvider';

const formatValue = (value, fallback = '—') => {
  if (value === null || value === undefined) {
    return fallback;
  }

  const normalized = String(value).trim();
  return normalized || fallback;
};

const parseMoney = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number.parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatMoney = (value) => `${new Intl.NumberFormat('fi-FI', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(value)} €`;

const hasElectricity = (electricityType) => {
  const normalized = String(electricityType ?? '').trim().toLowerCase();
  return normalized !== '' && normalized !== 'ei sähköä' && normalized !== 'ei sahkoa';
};

const getReservationTotal = (company) => {
  const directTotal = parseMoney(company?.total);

  if (directTotal !== null) {
    return directTotal;
  }

  return (Array.isArray(company?.spots) ? company.spots : []).reduce((sum, spot) => {
    const nextPrice = parseMoney(spot?.price ?? spot?.fixedPrice ?? spot?.prize) ?? 0;
    return sum + nextPrice;
  }, 0);
};

const buildLink = (value) => {
  const normalized = String(value ?? '').trim();

  if (!normalized) {
    return '';
  }

  return /^https?:\/\//i.test(normalized) ? normalized : `https://${normalized}`;
};

const SPOT_STATUS_FREE = process.env.REACT_APP_SPOT_STATUS_FREE ?? 'Vapaa';
const ADMIN_COMPANIES_ENDPOINT = process.env.REACT_APP_ADMIN_COMPANIES_ENDPOINT ?? '/admin/getCompanies';
const ADMIN_EDIT_COMPANY_ENDPOINT = process.env.REACT_APP_ADMIN_EDIT_COMPANY_ENDPOINT ?? '/admin/editCompany';
const ADMIN_EDIT_USER_ENDPOINT = process.env.REACT_APP_ADMIN_EDIT_USER_ENDPOINT ?? '/admin/editUser';
const ADMIN_EDIT_RESERVATION_ENDPOINT = process.env.REACT_APP_ADMIN_EDIT_RESERVATION_ENDPOINT ?? '/admin/EditReservation';
const ADMIN_REMOVE_USER_FROM_COMPANY_ENDPOINT = process.env.REACT_APP_ADMIN_REMOVE_USER_FROM_COMPANY_ENDPOINT ?? '/admin/removeUserFromCompany';
const MAP_IMAGE_URL = process.env.REACT_APP_MAP_IMAGE_URL ?? 'https://lauri.lcmynamaki.fi/kartta.svg';
const MAP_VIEWBOX = { minX: 0, minY: 0, width: 1122.52, height: 736.18 };
const MAP_STATUS_COLOURS = {
  free: '#66ff00',
  locked: '#f59e0b',
  reserved: '#ef4444',
  paid: '#2563eb',
  selected: '#06b6d4',
  border: '#0f172a',
};
const ELECTRICITY_OPTIONS = ['Ei sähköä', '230V', '400V'];
const RESERVATION_STATUS_OPTIONS = ['Ennakkovaraus', 'Lukittu', 'Varattu', 'Laskutettu', 'Maksu paikalla', 'Maksettu', 'Sähkö maksamatta'];
const STATUS_ORDER = ['Ennakkovaraus', 'Varattu', 'Laskutettu', 'Maksu paikalla', 'Maksettu'];
const OVERDUE_STATUS = 'Erääntyneet';
const FILTER_STATUS_ORDER = ['Ennakkovaraus', 'Varattu', 'Laskutettu', OVERDUE_STATUS, 'Maksu paikalla', 'Maksettu'];

const getStatusSortIndex = (status) => {
  const index = FILTER_STATUS_ORDER.findIndex((item) => item.toLowerCase() === String(status ?? '').toLowerCase());
  return index === -1 ? FILTER_STATUS_ORDER.length : index;
};

const sortStatuses = (statuses = []) => [...statuses].sort((a, b) => {
  const statusDiff = getStatusSortIndex(a) - getStatusSortIndex(b);
  return statusDiff !== 0 ? statusDiff : String(a).localeCompare(String(b), 'fi');
});

const getSpotStatusVisuals = (spot, selected = false) => {
  if (selected) {
    return {
      fill: MAP_STATUS_COLOURS.selected,
      stroke: '#0f766e',
      opacity: 0.82,
    };
  }

  const normalizedStatus = String(spot?.status ?? '').trim().toLowerCase();

  if (normalizedStatus === 'maksettu') {
    return { fill: MAP_STATUS_COLOURS.paid, stroke: '#1d4ed8', opacity: 0.72 };
  }

  if (normalizedStatus === 'ennakkovaraus' || normalizedStatus === 'lukittu') {
    return { fill: MAP_STATUS_COLOURS.locked, stroke: '#b45309', opacity: 0.72 };
  }

  if (normalizedStatus === 'varattu' || normalizedStatus === 'laskutettu' || normalizedStatus === 'maksu paikalla') {
    return { fill: MAP_STATUS_COLOURS.reserved, stroke: '#b91c1c', opacity: 0.72 };
  }

  if (isFreeSpotStatus(spot?.status)) {
    return { fill: MAP_STATUS_COLOURS.free, stroke: '#166534', opacity: 0.62 };
  }

  return { fill: '#94a3b8', stroke: '#475569', opacity: 0.65 };
};

const isFreeSpotStatus = (status) => {
  const normalizedStatus = String(status ?? '').trim().toLowerCase();
  return normalizedStatus === String(SPOT_STATUS_FREE).trim().toLowerCase() || normalizedStatus === 'vapaa';
};

const getSpotCoordinates = (spot) => {
  const source = spot?.coordinates;

  if (Array.isArray(source)) {
    return source.map((value) => Number(value)).filter((value) => Number.isFinite(value));
  }

  if (typeof source === 'string') {
    return source
      .replace('[', '')
      .replace(']', '')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));
  }

  return [];
};

const hasValidSpotCoordinates = (spot) => getSpotCoordinates(spot).length >= 6;

const getSpotCenter = (coordinates) => {
  const xCoordinates = coordinates.filter((_, index) => index % 2 === 0);
  const yCoordinates = coordinates.filter((_, index) => index % 2 === 1);

  if (!xCoordinates.length || !yCoordinates.length) {
    return { x: MAP_VIEWBOX.width / 2, y: MAP_VIEWBOX.height / 2 };
  }

  return {
    x: (Math.min(...xCoordinates) + Math.max(...xCoordinates)) / 2,
    y: (Math.min(...yCoordinates) + Math.max(...yCoordinates)) / 2,
  };
};

const getMapViewBox = () => MAP_VIEWBOX;

const emptyCompanyForm = {
  name: '',
  invoicingEmail: '',
  phone: '',
  publicEmail: '',
  publicPhone: '',
  address: '',
  address2: '',
  postalcode: '',
  city: '',
  homepage: '',
  facebook: '',
  instagram: '',
  description: '',
};

const emptyContactForm = {
  name: '',
  phone: '',
  email: '',
  mainUser: 1,
};

const emptySpotForm = {
  electricityType: 'Ei sähköä',
  fixedPrice: '',
  dueDate: '',
  status: 'Ennakkovaraus',
  newSpotID: '',
  invoiceReference: '',
};

const getSpotKey = (spot) => `${spot?.spotID ?? spot?.id ?? spot?.spotName ?? spot?.name ?? ''}`;
const getContactKey = (contact) => `${contact?.userID ?? contact?.email ?? contact?.name ?? ''}`;
const getSpotPrice = (spot) => parseMoney(spot?.price ?? spot?.fixedPrice ?? spot?.prize) ?? 0;

const normalizeSpotSelection = (spot = {}) => ({
  ...spot,
  name: spot?.name ?? spot?.spotName ?? '',
  spotName: spot?.spotName ?? spot?.name ?? '',
  electricityType: spot?.electricityType ?? 'Ei sähköä',
});

const normalizeCompanyOption = (company = {}) => ({
  companyID: company?.companyID,
  company: company?.company ?? company?.name ?? 'Tuntematon yritys',
  invoicingEmail: company?.invoicingEmail ?? '',
  publicEmail: company?.publicEmail ?? '',
  phone: company?.phone ?? '',
  publicPhone: company?.publicPhone ?? '',
  address: company?.address ?? '',
  address2: company?.address2 ?? '',
  postalcode: company?.postalcode ?? '',
  city: company?.city ?? '',
  description: company?.description ?? '',
  homepage: company?.homepage ?? '',
  facebook: company?.facebook ?? '',
  instagram: company?.instagram ?? '',
  contacts: Array.isArray(company?.contacts) ? company.contacts : [],
});

const getDefaultContactId = (contacts = []) => {
  const defaultContact = contacts.find((contact) => Number(contact?.mainUser) === 1) ?? contacts[0];
  return defaultContact ? getContactKey(defaultContact) : '';
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim());

const normalizeDateToDisplay = (value) => {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    return '';
  }

  const isoMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
  }

  return normalized;
};

const toDateInputValue = (value) => {
  if (!value) {
    return '';
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    return String(value);
  }

  const match = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : '';
};

const formatDateForApi = (value) => {
  if (!value) {
    return '';
  }

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value);
};

const parseDueDate = (value) => {
  if (!value) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const match = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) {
    return null;
  }

  const parsed = new Date(`${match[3]}-${match[2]}-${match[1]}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isInvoiceOverdue = (invoice) => {
  const dueDate = parseDueDate(invoice?.dueDate);
  if (!dueDate) {
    return false;
  }

  const statuses = invoice?.statuses?.length ? invoice.statuses : [];
  const isPaid = statuses.some((status) => String(status ?? '').trim().toLowerCase() === 'maksettu');
  if (isPaid) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

const getInvoiceFilterStatuses = (invoice) => {
  const statuses = invoice?.statuses?.length ? invoice.statuses : ['Ei statusta'];
  return isInvoiceOverdue(invoice) ? sortStatuses([...statuses, OVERDUE_STATUS]) : statuses;
};

const getSpotBasePrice = (spot) => {
  const fixedPrice = parseMoney(spot?.fixedPrize ?? spot?.fixedPrice);

  if (fixedPrice !== null) {
    return fixedPrice;
  }

  return parseMoney(spot?.prize ?? spot?.price) ?? 0;
};

const getElectricityLinePrice = (spot) => {
  const fixedPrice = parseMoney(spot?.fixedPrize ?? spot?.fixedPrice);

  if (fixedPrice !== null) {
    return 0;
  }

  return hasElectricity(spot?.electricityType) ? 15 : 0;
};

const calculateSpotTotal = (spot) => getSpotBasePrice(spot) + getElectricityLinePrice(spot);

const getInvoiceKey = (companyID, invoiceReference) => `${companyID ?? 'company'}-${invoiceReference ?? 'invoice'}`;
const hasUsableInvoiceReference = (invoiceReference) => {
  const normalized = String(invoiceReference ?? '').trim();
  return normalized !== '' && !normalized.toLowerCase().startsWith('ei viitettä');
};

const ManageReservations = () => {
  const [reservationRows, setReservationRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingReservation, setDeletingReservation] = useState(false);
  const [editCompanyTarget, setEditCompanyTarget] = useState(null);
  const [editCompanyForm, setEditCompanyForm] = useState(emptyCompanyForm);
  const [savingCompanyEdit, setSavingCompanyEdit] = useState(false);
  const [editContactTarget, setEditContactTarget] = useState(null);
  const [editContactForm, setEditContactForm] = useState(emptyContactForm);
  const [savingContactEdit, setSavingContactEdit] = useState(false);
  const [editSpotTarget, setEditSpotTarget] = useState(null);
  const [editSpotForm, setEditSpotForm] = useState(emptySpotForm);
  const [savingSpotEdit, setSavingSpotEdit] = useState(false);
  const [removeContactTarget, setRemoveContactTarget] = useState(null);
  const [removingContact, setRemovingContact] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [companyMode, setCompanyMode] = useState('existing');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [newCompany, setNewCompany] = useState(emptyCompanyForm);
  const [contactMode, setContactMode] = useState('existing');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [newContact, setNewContact] = useState(emptyContactForm);
  const [selectedSpots, setSelectedSpots] = useState([]);
  const [sendInvoiceByEmail, setSendInvoiceByEmail] = useState(false);
  const [spotPickerOpen, setSpotPickerOpen] = useState(false);
  const [availableSpots, setAvailableSpots] = useState([]);
  const [spotPickerSelection, setSpotPickerSelection] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loadingCompanyOptions, setLoadingCompanyOptions] = useState(false);
  const [savingReservation, setSavingReservation] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editDueDate, setEditDueDate] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [sendingReminderKey, setSendingReminderKey] = useState('');
  const [sendingInvoiceKey, setSendingInvoiceKey] = useState('');
  const [showSendInvoiceModal, setShowSendInvoiceModal] = useState(false);
  const [sendInvoiceModalData, setSendInvoiceModalData] = useState({
    company: null,
    invoice: null,
  });
  const [sendInvoiceLoading, setSendInvoiceLoading] = useState(false);
  const [reminderSentByInvoice, setReminderSentByInvoice] = useState({});
  const [createInvoiceTarget, setCreateInvoiceTarget] = useState(null);
  const [createInvoiceDueDate, setCreateInvoiceDueDate] = useState('');
  const [createInvoiceSpotIds, setCreateInvoiceSpotIds] = useState([]);
  const [createInvoiceSendEmail, setCreateInvoiceSendEmail] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUserOptions, setLoadingUserOptions] = useState(true);
  const api = useAxios();
  const apiRef = useRef(api);
  const { auth, isAdmin, setAuth } = useAuth();

  apiRef.current = api;

  const userIsAdmin = Boolean(isAdmin?.() ?? auth?.isAdmin ?? auth?.user?.isAdmin);

  const loadReservations = useCallback(async (showLoading = true) => {
    if (!userIsAdmin) {
      setLoading(false);
      return;
    }

    if (showLoading) {
      setLoading(true);
    }

    try {
      const response = await apiRef.current.get('/admin/getReservations', {
        params: { activityID: ACTIVITY_ID, activityId: ACTIVITY_ID },
        withCredentials: true,
      });

      const nextRows = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.reservations) ? response.data.reservations : []);

      setReservationRows(nextRows);
    } catch (err) {
      console.error('getReservations failed', err);
      toast.error('Varausten haku epäonnistui');
      setReservationRows([]);
    } finally {
      setLoading(false);
    }
  }, [userIsAdmin]);

  const loadAvailableCompanies = useCallback(async () => {
    if (!userIsAdmin) {
      setAllCompanies([]);
      return [];
    }

    setLoadingCompanyOptions(true);

    try {
      const response = await apiRef.current.get(ADMIN_COMPANIES_ENDPOINT, {
        params: { active: 1, includeContacts: 1 },
        withCredentials: true,
      });

      const nextCompanies = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.companies) ? response.data.companies : []);

      setAllCompanies(nextCompanies);
      return nextCompanies;
    } catch (err) {
      console.warn('Admin companies endpoint unavailable, using fallback company list instead.', err);
      setAllCompanies([]);
      return [];
    } finally {
      setLoadingCompanyOptions(false);
    }
  }, [userIsAdmin]);

  const loadAllUsers = useCallback(async () => {
    if (!userIsAdmin) {
      setAllUsers([]);
      return;
    }

    setLoadingUserOptions(true);

    try {
      const response = await apiRef.current.get('/admin/GetUsers', {
        withCredentials: true,
      });

      const nextUsers = Array.isArray(response?.data) ? response.data : [];
      setAllUsers(nextUsers);
    } catch (err) {
      console.warn('Admin GetUsers endpoint unavailable.', err);
      setAllUsers([]);
    } finally {
      setLoadingUserOptions(false);
    }
  }, [userIsAdmin]);

  const loadAvailableSpots = useCallback(async () => {
    setLoadingSpots(true);

    try {
      const response = await apiRef.current.get(`/spots?activityID=${ACTIVITY_ID}`);
      const nextSpots = Array.isArray(response?.data) ? response.data : [];
      setAvailableSpots(nextSpots);
      return nextSpots;
    } catch (err) {
      console.error('loadAvailableSpots failed', err);
      toast.error('Paikkojen haku epäonnistui');
      setAvailableSpots([]);
      return [];
    } finally {
      setLoadingSpots(false);
    }
  }, []);

  const loadInvoiceMetadata = useCallback(async () => {
    if (!userIsAdmin) {
      return;
    }

    try {
      const response = await apiRef.current.get('/admin/getInvoices', {
        params: { activityID: ACTIVITY_ID, activityId: ACTIVITY_ID },
        withCredentials: true,
      });

      const rows = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.invoices) ? response.data.invoices : []);

      const nextReminderMap = {};

      rows.forEach((row) => {
        const invoiceReference = String(row?.invoiceReference ?? '').trim();
        const companyID = row?.companyID;

        if (!invoiceReference || companyID == null || !row?.reminderSent) {
          return;
        }

        nextReminderMap[getInvoiceKey(companyID, invoiceReference)] = row.reminderSent;
      });

      if (Object.keys(nextReminderMap).length) {
        setReminderSentByInvoice((prev) => ({
          ...prev,
          ...nextReminderMap,
        }));
      }
    } catch (err) {
      console.warn('getInvoices metadata failed', err);
    }
  }, [userIsAdmin]);

  useEffect(() => {
    if (!userIsAdmin) {
      setLoading(false);
      return;
    }

    loadReservations();
    loadInvoiceMetadata();
    loadAvailableCompanies();
    loadAllUsers();
  }, [loadAllUsers, loadAvailableCompanies, loadInvoiceMetadata, loadReservations, userIsAdmin]);

  const companies = useMemo(() => (
    reservationRows
      .map((company) => {
        const companySpots = Array.isArray(company?.spots) ? company.spots : [];
        const invoiceMap = new Map();

        companySpots.forEach((spot) => {
          const invoiceReference = String(spot?.invoiceReference ?? '').trim() || 'Ei viitettä';
          const invoiceKey = getInvoiceKey(company?.companyID, invoiceReference);

          if (!invoiceMap.has(invoiceReference)) {
            invoiceMap.set(invoiceReference, {
              invoiceReference,
              dueDate: spot?.dueDate ?? '',
              statuses: [],
              spots: [],
              reminderSent: reminderSentByInvoice[invoiceKey] ?? spot?.reminderSent ?? null,
            });
          }

          const invoice = invoiceMap.get(invoiceReference);
          invoice.spots.push(spot);

          if (!invoice.dueDate && spot?.dueDate) {
            invoice.dueDate = spot.dueDate;
          }

          if (spot?.status) {
            invoice.statuses.push(spot.status);
          }
        });

        const invoices = Array.from(invoiceMap.values())
          .map((invoice) => ({
            ...invoice,
            statuses: sortStatuses(Array.from(new Set(invoice.statuses.filter(Boolean)))),
            total: invoice.spots.reduce((sum, spot) => sum + calculateSpotTotal(spot), 0),
          }))
          .sort((a, b) => String(a?.invoiceReference ?? '').localeCompare(String(b?.invoiceReference ?? ''), 'fi'));

        return {
          ...company,
          contacts: Array.isArray(company?.contacts) ? company.contacts : [],
          spots: companySpots,
          statuses: sortStatuses(Array.from(new Set(companySpots.map((spot) => spot?.status).filter(Boolean)))),
          addressLines: [
            company?.address,
            company?.address2,
            [company?.postalcode, company?.city].filter(Boolean).join(' '),
          ].filter(Boolean),
          invoices,
          totalAmount: getReservationTotal(company),
        };
      })
      .sort((a, b) => String(a?.company ?? '').localeCompare(String(b?.company ?? ''), 'fi'))
  ), [reminderSentByInvoice, reservationRows]);

  const availableCompanies = useMemo(() => {
    const companyMap = new Map();

    const mergeCompany = (sourceCompany) => {
      const normalizedCompany = normalizeCompanyOption(sourceCompany);

      if (normalizedCompany.companyID == null) {
        return;
      }

      const key = `${normalizedCompany.companyID}`;
      const existing = companyMap.get(key);

      if (!existing) {
        companyMap.set(key, normalizedCompany);
        return;
      }

      const mergedContacts = [...(existing.contacts ?? [])];
      (normalizedCompany.contacts ?? []).forEach((contact) => {
        const contactKey = getContactKey(contact);
        if (contactKey && !mergedContacts.some((item) => getContactKey(item) === contactKey)) {
          mergedContacts.push(contact);
        }
      });

      companyMap.set(key, {
        ...existing,
        ...normalizedCompany,
        company: existing.company || normalizedCompany.company,
        invoicingEmail: existing.invoicingEmail || normalizedCompany.invoicingEmail,
        phone: existing.phone || normalizedCompany.phone,
        publicEmail: existing.publicEmail || normalizedCompany.publicEmail,
        publicPhone: existing.publicPhone || normalizedCompany.publicPhone,
        address: existing.address || normalizedCompany.address,
        address2: existing.address2 || normalizedCompany.address2,
        postalcode: existing.postalcode || normalizedCompany.postalcode,
        city: existing.city || normalizedCompany.city,
        description: existing.description || normalizedCompany.description,
        homepage: existing.homepage || normalizedCompany.homepage,
        facebook: existing.facebook || normalizedCompany.facebook,
        instagram: existing.instagram || normalizedCompany.instagram,
        contacts: mergedContacts,
      });
    };

    companies.forEach(mergeCompany);
    (auth?.companies ?? []).forEach(mergeCompany);
    (allCompanies ?? []).forEach(mergeCompany);

    return Array.from(companyMap.values()).sort((a, b) => String(a?.company ?? '').localeCompare(String(b?.company ?? ''), 'fi'));
  }, [allCompanies, auth?.companies, companies]);

  const filteredCompanies = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return companies
      .map((company) => {
        const filteredInvoices = (company?.invoices ?? []).filter((invoice) => {
          const invoiceStatuses = getInvoiceFilterStatuses(invoice);
          return selectedStatuses.length === 0 || invoiceStatuses.some((status) => selectedStatuses.includes(status));
        });

        const searchHaystack = [
          company?.company,
          company?.invoicingEmail,
          company?.publicEmail,
          company?.phone,
          company?.publicPhone,
          company?.address,
          company?.address2,
          company?.postalcode,
          company?.city,
          company?.description,
          company?.homepage,
          company?.facebook,
          company?.instagram,
          ...(company?.statuses ?? []),
          ...(company?.spotNames ?? []),
          ...(company?.contacts ?? []).flatMap((contact) => [contact?.name, contact?.phone, contact?.email]),
          ...(company?.spots ?? []).flatMap((spot) => [
            spot?.spotName,
            spot?.status,
            spot?.invoiceReference,
            spot?.yourReference,
            spot?.dueDate,
            spot?.electricityType,
            spot?.size,
            spot?.coordinates,
            spot?.price,
            spot?.fixedPrice,
            spot?.prize,
          ]),
          ...filteredInvoices.flatMap((invoice) => [invoice?.invoiceReference, invoice?.dueDate, ...(invoice?.statuses ?? [])]),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        const matchesSearch = !normalizedSearch || searchHaystack.includes(normalizedSearch);
        return {
          ...company,
          filteredInvoices,
          matchesSearch,
        };
      })
      .filter((company) => company.matchesSearch && (selectedStatuses.length === 0 || company.filteredInvoices.length > 0));
  }, [companies, searchTerm, selectedStatuses]);

  const statusSummary = useMemo(() => {
    const summaryMap = new Map();

    companies.forEach((company) => {
      (company?.invoices ?? []).forEach((invoice) => {
        getInvoiceFilterStatuses(invoice).forEach((status) => {
          const current = summaryMap.get(status) ?? { status, count: 0, total: 0 };
          current.count += 1;
          current.total += invoice.total;
          summaryMap.set(status, current);
        });
      });
    });

    return Array.from(summaryMap.values()).sort((a, b) => {
      const statusDiff = getStatusSortIndex(a.status) - getStatusSortIndex(b.status);
      return statusDiff !== 0 ? statusDiff : a.status.localeCompare(b.status, 'fi');
    });
  }, [companies]);

  const freeSpots = useMemo(
    () => availableSpots.filter((spot) => isFreeSpotStatus(spot?.status)),
    [availableSpots]
  );

  const mappableSpots = useMemo(
    () => availableSpots.filter((spot) => hasValidSpotCoordinates(spot)),
    [availableSpots]
  );

  const listOnlyFreeSpots = useMemo(
    () => freeSpots.filter((spot) => !hasValidSpotCoordinates(spot)),
    [freeSpots]
  );

  const selectedCompany = useMemo(
    () => availableCompanies.find((company) => `${company?.companyID ?? ''}` === `${selectedCompanyId}`) ?? null,
    [availableCompanies, selectedCompanyId]
  );

  const mapViewBox = useMemo(() => getMapViewBox(), []);

  const availableContacts = useMemo(
    () => (Array.isArray(selectedCompany?.contacts) ? selectedCompany.contacts : []),
    [selectedCompany]
  );

  const selectedExistingContact = useMemo(() => {
    if (!selectedContactId) return null;
    const fromContacts = availableContacts.find((contact) => getContactKey(contact) === selectedContactId) ?? null;
    if (fromContacts) return fromContacts;
    return allUsers.find((user) => `${user?.userID ?? ''}` === `${selectedContactId}`) ?? null;
  }, [availableContacts, allUsers, selectedContactId]);

  const handleCompanySelection = (companyId) => {
    setSelectedCompanyId(companyId);

    if (!companyId) {
      setContactMode('existing');
      setSelectedContactId('');
      setNewContact(emptyContactForm);
      return;
    }

    const nextCompany = availableCompanies.find((company) => `${company?.companyID ?? ''}` === `${companyId}`) ?? null;
    const nextContacts = Array.isArray(nextCompany?.contacts) ? nextCompany.contacts : [];

    if (nextContacts.length) {
      setContactMode('existing');
      setSelectedContactId(getDefaultContactId(nextContacts));
    } else {
      setContactMode('existing');
      setSelectedContactId('');
      setNewContact(emptyContactForm);
    }
  };

  const openAddReservationModal = () => {
    setIsAddModalOpen(true);
    setSearchTerm('');
    setDeleteTarget(null);
    setCompanyMode('existing');
    setSelectedCompanyId('');
    setNewCompany(emptyCompanyForm);
    setContactMode('existing');
    setSelectedContactId('');
    setNewContact(emptyContactForm);
    setSelectedSpots([]);
    setSendInvoiceByEmail(false);
    setSpotPickerSelection([]);
    setSpotPickerOpen(false);
    loadAvailableCompanies();
    loadAllUsers();
  };

  const closeAddReservationModal = () => {
    if (savingReservation) {
      return;
    }

    setIsAddModalOpen(false);
    setSpotPickerOpen(false);
  };

  const openEditCompanyModal = (company) => {
    setEditCompanyTarget(company);
    setEditCompanyForm({
      name: company?.company ?? company?.name ?? '',
      invoicingEmail: company?.invoicingEmail ?? '',
      phone: company?.phone ?? '',
      publicEmail: company?.publicEmail ?? '',
      publicPhone: company?.publicPhone ?? '',
      address: company?.address ?? '',
      address2: company?.address2 ?? '',
      postalcode: company?.postalcode ?? '',
      city: company?.city ?? '',
      homepage: company?.homepage ?? '',
      facebook: company?.facebook ?? '',
      instagram: company?.instagram ?? '',
      description: company?.description ?? '',
    });
  };

  const closeEditCompanyModal = () => {
    if (savingCompanyEdit) {
      return;
    }

    setEditCompanyTarget(null);
    setEditCompanyForm(emptyCompanyForm);
  };

  const handleEditCompanyFieldChange = (field, value) => {
    setEditCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveCompanyEdits = async () => {
    if (!editCompanyTarget?.companyID) {
      return;
    }

    if (!editCompanyForm.name.trim()) {
      toast.warn('Anna yrityksen nimi');
      return;
    }

    if (!isValidEmail(editCompanyForm.invoicingEmail)) {
      toast.warn('Anna kelvollinen laskutussähköposti');
      return;
    }

    if (!editCompanyForm.phone.trim()) {
      toast.warn('Anna yrityksen puhelinnumero');
      return;
    }

    if (editCompanyForm.publicEmail && !isValidEmail(editCompanyForm.publicEmail)) {
      toast.warn('Anna kelvollinen julkinen sähköposti');
      return;
    }

    const payload = {
      companyID: editCompanyTarget.companyID,
      name: editCompanyForm.name.trim(),
      company: editCompanyForm.name.trim(),
      phone: editCompanyForm.phone.trim(),
      invoicingEmail: editCompanyForm.invoicingEmail.trim(),
      address: editCompanyForm.address.trim(),
      address2: editCompanyForm.address2.trim(),
      postalcode: editCompanyForm.postalcode.trim(),
      city: editCompanyForm.city.trim(),
      publicEmail: editCompanyForm.publicEmail.trim(),
      publicPhone: editCompanyForm.publicPhone.trim(),
      homepage: editCompanyForm.homepage.trim(),
      facebook: editCompanyForm.facebook.trim(),
      instagram: editCompanyForm.instagram.trim(),
      description: editCompanyForm.description.trim(),
    };

    try {
      setSavingCompanyEdit(true);

      try {
        await apiRef.current.post(
          ADMIN_EDIT_COMPANY_ENDPOINT,
          JSON.stringify({ company: payload }),
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
          }
        );
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          await apiRef.current.post(
            '/editCompany',
            JSON.stringify({ company: payload }),
            {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true,
            }
          );
        } else {
          throw err;
        }
      }

      setReservationRows((prev) => prev.map((company) => (
        `${company?.companyID ?? ''}` === `${payload.companyID}`
          ? { ...company, ...payload }
          : company
      )));

      setAllCompanies((prev) => prev.map((company) => (
        `${company?.companyID ?? ''}` === `${payload.companyID}`
          ? { ...company, ...payload }
          : company
      )));

      if (typeof setAuth === 'function' && Array.isArray(auth?.companies)) {
        setAuth({
          ...auth,
          companies: auth.companies.map((company) => (
            `${company?.companyID ?? ''}` === `${payload.companyID}`
              ? { ...company, ...payload }
              : company
          )),
        });
      }

      toast.success('Yrityksen tiedot päivitetty');
      closeEditCompanyModal();
    } catch (err) {
      console.error('editCompany failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Yrityksen tietojen tallennus epäonnistui';
      toast.error(errorMessage);
    } finally {
      setSavingCompanyEdit(false);
    }
  };

  const openEditContactModal = (company, contact) => {
    setEditContactTarget({
      ...contact,
      companyID: company?.companyID,
      companyName: company?.company,
    });
    setEditContactForm({
      name: contact?.name ?? '',
      phone: contact?.phone ?? '',
      email: contact?.email ?? '',
      mainUser: Number(contact?.mainUser) === 1 ? 1 : 0,
    });
  };

  const closeEditContactModal = () => {
    if (savingContactEdit) {
      return;
    }

    setEditContactTarget(null);
    setEditContactForm(emptyContactForm);
  };

  const handleEditContactFieldChange = (field, value) => {
    setEditContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveContactEdits = async () => {
    if (!editContactTarget?.userID) {
      return;
    }

    const trimmedName = editContactForm.name.trim();
    const trimmedPhone = editContactForm.phone.trim();
    const trimmedEmail = editContactForm.email.trim();

    if (!trimmedName) {
      toast.warn('Anna yhteyshenkilön nimi');
      return;
    }

    if (!trimmedPhone) {
      toast.warn('Anna yhteyshenkilön puhelinnumero');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      toast.warn('Anna kelvollinen sähköposti');
      return;
    }

    const payload = {
      userID: editContactTarget.userID,
      companyID: editContactTarget.companyID,
      name: trimmedName,
      phone: trimmedPhone,
      email: trimmedEmail,
      mainUser: Number(editContactForm.mainUser) === 1 ? 1 : 0,
    };

    try {
      setSavingContactEdit(true);

      try {
        await apiRef.current.post(
          ADMIN_EDIT_USER_ENDPOINT,
          JSON.stringify(payload),
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
          }
        );
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          await apiRef.current.post(
            '/editUser',
            JSON.stringify(payload),
            {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true,
            }
          );
        } else {
          throw err;
        }
      }

      const updateContactList = (contacts = []) => contacts.map((contact) => (
        `${contact?.userID ?? ''}` === `${payload.userID}`
          ? { ...contact, ...payload }
          : contact
      ));

      setReservationRows((prev) => prev.map((company) => (
        `${company?.companyID ?? ''}` === `${payload.companyID}`
          ? { ...company, contacts: updateContactList(Array.isArray(company?.contacts) ? company.contacts : []) }
          : company
      )));

      setAllCompanies((prev) => prev.map((company) => (
        `${company?.companyID ?? ''}` === `${payload.companyID}`
          ? { ...company, contacts: updateContactList(Array.isArray(company?.contacts) ? company.contacts : []) }
          : company
      )));

      if (typeof setAuth === 'function') {
        const authCompanies = Array.isArray(auth?.companies) ? auth.companies : [];
        const updatedAuth = {
          ...auth,
          companies: authCompanies.map((company) => (
            `${company?.companyID ?? ''}` === `${payload.companyID}`
              ? { ...company, contacts: updateContactList(Array.isArray(company?.contacts) ? company.contacts : []) }
              : company
          )),
        };

        if (`${auth?.user?.userID ?? ''}` === `${payload.userID}`) {
          updatedAuth.user = {
            ...auth?.user,
            name: payload.name,
            fullName: payload.name,
            phone: payload.phone,
            phoneNumber: payload.phone,
            email: payload.email,
          };
        }

        setAuth(updatedAuth);
      }

      toast.success('Yhteyshenkilön tiedot päivitetty');
      closeEditContactModal();
    } catch (err) {
      console.error('editUser failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Yhteyshenkilön tallennus epäonnistui';
      toast.error(errorMessage);
    } finally {
      setSavingContactEdit(false);
    }
  };

  const openEditSpotModal = (company, spot) => {
    setEditSpotTarget({
      companyID: company?.companyID,
      companyName: company?.company,
      reservationID: spot?.reservationID,
      spotID: spot?.spotID,
      spotName: spot?.spotName ?? spot?.name ?? '',
    });

    setEditSpotForm({
      electricityType: spot?.electricityType ?? 'Ei sähköä',
      fixedPrice: spot?.fixedPrice ?? '',
      dueDate: normalizeDateToDisplay(spot?.dueDate),
      status: spot?.status ?? 'Ennakkovaraus',
      newSpotID: String(spot?.spotID ?? ''),
      invoiceReference: spot?.invoiceReference ?? '',
    });

    if (availableSpots.length === 0 && !loadingSpots) {
      loadAvailableSpots();
    }
  };

  const closeEditSpotModal = () => {
    if (savingSpotEdit) {
      return;
    }

    setEditSpotTarget(null);
    setEditSpotForm(emptySpotForm);
  };

  const handleEditSpotFieldChange = (field, value) => {
    setEditSpotForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSpotEdits = async () => {
    if (!editSpotTarget?.reservationID || !editSpotTarget?.spotID) {
      return;
    }

    const normalizedElectricityType = (editSpotForm.electricityType || 'Ei sähköä').trim() || 'Ei sähköä';
    const normalizedStatus = (editSpotForm.status || '').trim();
    const normalizedDueDate = String(editSpotForm.dueDate ?? '').trim();
    const normalizedFixedPrice = String(editSpotForm.fixedPrice ?? '').trim();

    if (!normalizedStatus) {
      toast.warn('Valitse status');
      return;
    }

    if (normalizedFixedPrice !== '' && Number.isNaN(Number.parseFloat(normalizedFixedPrice.replace(',', '.')))) {
      toast.warn('Anna fixedPrice numerona');
      return;
    }

    const normalizedNewSpotID = String(editSpotForm.newSpotID ?? '').trim();
    const normalizedInvoiceReference = String(editSpotForm.invoiceReference ?? '').trim();

    const payload = {
      activityID: ACTIVITY_ID,
      activityId: ACTIVITY_ID,
      companyID: editSpotTarget.companyID,
      reservationID: editSpotTarget.reservationID,
      spotID: editSpotTarget.spotID,
      newSpotID: normalizedNewSpotID !== '' && normalizedNewSpotID !== String(editSpotTarget.spotID) ? normalizedNewSpotID : undefined,
      invoiceReference: normalizedInvoiceReference,
      spot: {
        electricityType: normalizedElectricityType,
        fixedPrice: normalizedFixedPrice,
        dueDate: normalizedDueDate,
        status: normalizedStatus,
      },
    };

    try {
      setSavingSpotEdit(true);

      try {
        await apiRef.current.post(
          ADMIN_EDIT_RESERVATION_ENDPOINT,
          JSON.stringify(payload),
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
          }
        );
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          await apiRef.current.post(
            '/admin/editReservation',
            JSON.stringify(payload),
            {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true,
            }
          );
        } else {
          throw err;
        }
      }

      await loadReservations(false);

      toast.success('Paikan tiedot päivitetty');
      closeEditSpotModal();
    } catch (err) {
      console.error('editReservation failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Paikan tietojen tallennus epäonnistui';
      toast.error(errorMessage);
    } finally {
      setSavingSpotEdit(false);
    }
  };

  const openRemoveContactModal = (company, contact) => {
    setRemoveContactTarget({
      companyID: company?.companyID,
      companyName: company?.company,
      userID: contact?.userID,
      name: contact?.name,
      email: contact?.email,
    });
  };

  const closeRemoveContactModal = () => {
    if (removingContact) {
      return;
    }

    setRemoveContactTarget(null);
  };

  const handleRemoveContactFromCompany = async () => {
    if (!removeContactTarget?.companyID || !removeContactTarget?.userID) {
      return;
    }

    const payload = {
      companyID: removeContactTarget.companyID,
      userID: removeContactTarget.userID,
    };

    try {
      setRemovingContact(true);

      try {
        await apiRef.current.post(
          ADMIN_REMOVE_USER_FROM_COMPANY_ENDPOINT,
          JSON.stringify(payload),
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
          }
        );
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          await apiRef.current.post(
            '/removeUserFromCompany',
            JSON.stringify(payload),
            {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true,
            }
          );
        } else {
          throw err;
        }
      }

      const removeContactFromList = (contacts = []) => contacts.filter((contact) => `${contact?.userID ?? ''}` !== `${payload.userID}`);

      setReservationRows((prev) => prev.map((company) => (
        `${company?.companyID ?? ''}` === `${payload.companyID}`
          ? { ...company, contacts: removeContactFromList(Array.isArray(company?.contacts) ? company.contacts : []) }
          : company
      )));

      setAllCompanies((prev) => prev.map((company) => (
        `${company?.companyID ?? ''}` === `${payload.companyID}`
          ? { ...company, contacts: removeContactFromList(Array.isArray(company?.contacts) ? company.contacts : []) }
          : company
      )));

      if (typeof setAuth === 'function' && Array.isArray(auth?.companies)) {
        setAuth({
          ...auth,
          companies: auth.companies.map((company) => (
            `${company?.companyID ?? ''}` === `${payload.companyID}`
              ? { ...company, contacts: removeContactFromList(Array.isArray(company?.contacts) ? company.contacts : []) }
              : company
          )),
        });
      }

      toast.success('Yhteyshenkilö poistettu yritykseltä');
      closeRemoveContactModal();
    } catch (err) {
      console.error('removeUserFromCompany failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Yhteyshenkilön poisto epäonnistui';
      toast.error(errorMessage);
    } finally {
      setRemovingContact(false);
    }
  };

  const openDeleteModal = (company, spot) => {
    setDeleteTarget({
      companyID: company?.companyID,
      companyName: company?.company,
      reservationID: spot?.reservationID,
      spotID: spot?.spotID,
      spotName: spot?.spotName ?? spot?.name,
      invoiceReference: spot?.invoiceReference,
    });
  };

  const closeDeleteModal = () => {
    if (deletingReservation) {
      return;
    }

    setDeleteTarget(null);
  };

  const handleDeleteReservation = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      setDeletingReservation(true);

      await apiRef.current.post(
        '/admin/deleteReservation',
        JSON.stringify({
          activityID: ACTIVITY_ID,
          activityId: ACTIVITY_ID,
          companyID: deleteTarget.companyID,
          reservationID: deleteTarget.reservationID,
          spotID: deleteTarget.spotID,
          invoiceReference: deleteTarget.invoiceReference,
          spot: {
            reservationID: deleteTarget.reservationID,
            spotID: deleteTarget.spotID,
            spotName: deleteTarget.spotName,
            invoiceReference: deleteTarget.invoiceReference,
          },
        }),
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );

      setReservationRows((prev) => prev
        .map((company) => {
          if (`${company?.companyID ?? ''}` !== `${deleteTarget?.companyID ?? ''}`) {
            return company;
          }

          const nextSpots = (Array.isArray(company?.spots) ? company.spots : []).filter((spot) => {
            if (deleteTarget?.reservationID != null && spot?.reservationID != null) {
              return `${spot.reservationID}` !== `${deleteTarget.reservationID}`;
            }

            return `${spot?.spotID ?? ''}` !== `${deleteTarget?.spotID ?? ''}`;
          });

          const nextStatuses = Array.from(new Set(nextSpots.map((spot) => spot?.status).filter(Boolean)));
          const nextSpotNames = nextSpots.map((spot) => spot?.spotName ?? spot?.name).filter(Boolean);
          const nextTotal = nextSpots.reduce((sum, spot) => sum + getSpotPrice(spot), 0);

          return {
            ...company,
            spots: nextSpots,
            statuses: nextStatuses,
            spotNames: nextSpotNames,
            total: nextTotal,
          };
        })
        .filter((company) => (Array.isArray(company?.spots) ? company.spots.length > 0 : true)));

      toast.success(`Varaus paikkaan ${deleteTarget.spotName} poistettu`);
      setDeleteTarget(null);
    } catch (err) {
      console.error('deleteReservation failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Varauksen poisto epäonnistui';
      toast.error(errorMessage);
    } finally {
      setDeletingReservation(false);
    }
  };

  const openSpotPicker = async () => {
    await loadAvailableSpots();
    setSpotPickerSelection(selectedSpots.map((spot) => getSpotKey(spot)));
    setSpotPickerOpen(true);
  };

  const toggleSpotInPicker = (spot) => {
    if (!isFreeSpotStatus(spot?.status)) {
      return;
    }

    const spotKey = getSpotKey(spot);

    setSpotPickerSelection((prev) => (
      prev.includes(spotKey)
        ? prev.filter((item) => item !== spotKey)
        : [...prev, spotKey]
    ));
  };

  const confirmSpotPickerSelection = () => {
    const nextSelectedSpots = freeSpots
      .filter((spot) => spotPickerSelection.includes(getSpotKey(spot)))
      .map((spot) => {
        const existing = selectedSpots.find((item) => getSpotKey(item) === getSpotKey(spot));
        return existing
          ? {
              ...normalizeSpotSelection(spot),
              electricityType: existing.electricityType,
              fixedPrice: existing.fixedPrice,
            }
          : normalizeSpotSelection(spot);
      })
      .sort((a, b) => String(a?.spotName ?? '').localeCompare(String(b?.spotName ?? ''), 'fi'));

    setSelectedSpots(nextSelectedSpots);
    setSpotPickerOpen(false);
  };

  const removeSelectedSpot = (spotKey) => {
    setSelectedSpots((prev) => prev.filter((spot) => getSpotKey(spot) !== spotKey));
  };

  const updateSelectedSpotElectricity = (spotKey, electricityType) => {
    setSelectedSpots((prev) => prev.map((spot) => (
      getSpotKey(spot) === spotKey ? { ...spot, electricityType } : spot
    )));
  };

  const updateSelectedSpotFixedPrice = (spotKey, fixedPrice) => {
    setSelectedSpots((prev) => prev.map((spot) => (
      getSpotKey(spot) === spotKey ? { ...spot, fixedPrice } : spot
    )));
  };

  const toggleStatus = (status) => {
    setSelectedStatuses((prev) => (
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    ));
  };

  const openEditInvoiceModal = (company, invoice) => {
    const currentStatuses = sortStatuses(invoice?.statuses?.length ? invoice.statuses : ['Ennakkovaraus']);

    setEditingInvoice({
      companyID: company?.companyID,
      companyName: company?.company,
      invoiceReference: invoice?.invoiceReference,
    });
    setEditDueDate(toDateInputValue(invoice?.dueDate));
    setEditStatus(currentStatuses[0] ?? STATUS_ORDER[0]);
  };

  const closeEditInvoiceModal = () => {
    if (savingInvoice) {
      return;
    }

    setEditingInvoice(null);
    setEditDueDate('');
    setEditStatus('');
  };

  const saveInvoiceChanges = async () => {
    if (!userIsAdmin || !editingInvoice) {
      return;
    }

    if (!editDueDate) {
      toast.warn('Valitse eräpäivä');
      return;
    }

    if (!editStatus) {
      toast.warn('Valitse status');
      return;
    }

    const formattedDueDate = formatDateForApi(editDueDate);

    try {
      setSavingInvoice(true);

      await apiRef.current.post(
        '/admin/changeInvoice',
        JSON.stringify({
          activityID: ACTIVITY_ID,
          activityId: ACTIVITY_ID,
          companyID: editingInvoice.companyID,
          invoiceReference: editingInvoice.invoiceReference,
          dueDate: formattedDueDate,
          status: editStatus,
          newStatus: editStatus,
        }),
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );

      setReservationRows((prev) => prev.map((company) => {
        if (`${company?.companyID ?? ''}` !== `${editingInvoice?.companyID ?? ''}`) {
          return company;
        }

        return {
          ...company,
          spots: (Array.isArray(company?.spots) ? company.spots : []).map((spot) => (
            `${spot?.invoiceReference ?? ''}` === `${editingInvoice?.invoiceReference ?? ''}`
              ? {
                  ...spot,
                  dueDate: formattedDueDate,
                  status: editStatus,
                }
              : spot
          )),
        };
      }));

      toast.success('Laskun tiedot tallennettu');
      closeEditInvoiceModal();
    } catch (err) {
      console.error('changeInvoice failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Laskun päivitys epäonnistui';
      toast.error(errorMessage);
    } finally {
      setSavingInvoice(false);
    }
  };

  const sendReminder = async (company, invoice) => {
    if (!userIsAdmin) {
      return;
    }

    if (!invoice?.invoiceReference || String(invoice.invoiceReference).startsWith('Ei viitettä')) {
      toast.warn('Laskulla ei ole viitenumeroa muistutusta varten.');
      return;
    }

    if (!isInvoiceOverdue(invoice)) {
      toast.warn('Muistutus voidaan lähettää vain erääntyneelle laskulle.');
      return;
    }

    const invoiceKey = getInvoiceKey(company?.companyID, invoice?.invoiceReference);

    try {
      setSendingReminderKey(invoiceKey);

      const response = await apiRef.current.post(
        '/admin/sendReminder',
        JSON.stringify({
          companyID: company?.companyID,
          invoiceReference: invoice?.invoiceReference,
        }),
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );

      const reminderSent = response?.data?.reminderSent ?? new Date().toISOString();
      setReminderSentByInvoice((prev) => ({
        ...prev,
        [invoiceKey]: reminderSent,
      }));

      toast.success(response?.data?.msg ?? 'Muistutus lähetetty onnistuneesti.');
    } catch (err) {
      console.error('sendReminder failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Muistutuksen lähetys epäonnistui';
      toast.error(errorMessage);
    } finally {
      setSendingReminderKey('');
    }
  };

  const openInvoice = (invoice) => {
    if (!hasUsableInvoiceReference(invoice?.invoiceReference)) {
      toast.warn('Laskulla ei ole avattavaa viitettä.');
      return;
    }

    const invoiceWindow = window.open('about:blank', '_blank');

    if (!invoiceWindow) {
      toast.error('Selaimesi esti uuden välilehden avaamisen. Salli ponnahdusikkunat ja yritä uudelleen.');
      return;
    }

    try {
      invoiceWindow.document.title = 'Ladataan laskua...';
      invoiceWindow.document.body.innerHTML = '<p style="font-family: sans-serif; padding: 16px;">Ladataan laskua...</p>';
    } catch (windowError) {
      // Ignore temporary document rendering failures and continue with fetch.
    }

    (async () => {
      try {
        const response = await apiRef.current.get('/admin/openInvoice', {
          params: {
            activityID: ACTIVITY_ID,
            activityId: ACTIVITY_ID,
            invoiceReference: String(invoice.invoiceReference).trim(),
          },
          responseType: 'blob',
          withCredentials: true,
        });

        const contentType = String(response?.headers?.['content-type'] ?? '').toLowerCase();
        if (!contentType.includes('application/pdf')) {
          const message = await response.data.text();
          throw new Error(message || 'Laskun avaaminen epäonnistui');
        }

        const blobUrl = URL.createObjectURL(response.data);
        invoiceWindow.location.href = blobUrl;

        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch (err) {
        console.error('openInvoice failed', err);
        invoiceWindow.close();

        const fallbackError = err?.response?.data;
        if (fallbackError instanceof Blob) {
          try {
            const errorText = await fallbackError.text();
            const parsed = JSON.parse(errorText);
            toast.error(parsed?.msg ?? 'Laskun avaaminen epäonnistui');
            return;
          } catch (parseError) {
            toast.error('Laskun avaaminen epäonnistui');
            return;
          }
        }

        const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Laskun avaaminen epäonnistui';
        toast.error(errorMessage);
      }
    })();
  };

  const sendInvoice = (company, invoice) => {
    openSendInvoiceModal(company, invoice);
  };

  const openSendInvoiceModal = (company, invoice) => {
    if (!hasUsableInvoiceReference(invoice?.invoiceReference)) {
      toast.warning('Laskulla ei ole viitenumeroa');
      return;
    }
    setSendInvoiceModalData({ company, invoice });
    setShowSendInvoiceModal(true);
  };

  const submitSendInvoice = async (sendMode) => {
    if (!sendInvoiceModalData.company || !sendInvoiceModalData.invoice) {
      toast.error('Virheellinen lasku');
      return;
    }

    setSendInvoiceLoading(true);
    try {
      const response = await apiRef.current.post(
        '/admin/sendInvoice',
        JSON.stringify({
          companyID: sendInvoiceModalData.company.companyID,
          invoiceReference: sendInvoiceModalData.invoice.invoiceReference,
          sendMode: sendMode,
        }),
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );

      if (response?.data?.success) {
        toast.success(response.data.msg || 'Lasku lähetetty onnistuneesti.');
        setShowSendInvoiceModal(false);
        setSendInvoiceModalData({ company: null, invoice: null });
        await loadInvoiceMetadata();
      }
    } catch (err) {
      console.error('submitSendInvoice failed', err);
      const errMsg = err?.response?.data?.msg || 'Laskun lähetys epäonnistui.';
      toast.error(errMsg);
    } finally {
      setSendInvoiceLoading(false);
    }
  };

  const openCreateInvoiceModal = (company) => {
    const unpaidSpots = (Array.isArray(company?.spots) ? company.spots : []).filter(
      (spot) => String(spot?.status ?? '').trim().toLowerCase() !== 'maksettu'
    );

    if (unpaidSpots.length === 0) {
      toast.warn('Yrityksellä ei ole maksamattomia paikkoja laskulle.');
      return;
    }

    const selectedSpotIds = unpaidSpots
      .map((spot) => Number(spot?.spotID))
      .filter((spotID) => Number.isFinite(spotID));

    const defaultDueDate = toDateInputValue(unpaidSpots[0]?.dueDate) || toDateInputValue(new Date().toISOString().slice(0, 10));

    setCreateInvoiceTarget({
      companyID: company?.companyID,
      companyName: company?.company,
      spots: unpaidSpots,
    });
    setCreateInvoiceSpotIds(selectedSpotIds);
    setCreateInvoiceDueDate(defaultDueDate);
    setCreateInvoiceSendEmail(false);
  };

  const closeCreateInvoiceModal = () => {
    if (creatingInvoice) {
      return;
    }

    setCreateInvoiceTarget(null);
    setCreateInvoiceSpotIds([]);
    setCreateInvoiceDueDate('');
    setCreateInvoiceSendEmail(false);
  };

  const toggleCreateInvoiceSpot = (spotID) => {
    setCreateInvoiceSpotIds((prev) => (
      prev.includes(spotID)
        ? prev.filter((id) => id !== spotID)
        : [...prev, spotID]
    ));
  };

  const handleCreateInvoiceForSpots = async () => {
    if (!createInvoiceTarget?.companyID) {
      return;
    }

    if (!createInvoiceDueDate) {
      toast.warn('Valitse eräpäivä');
      return;
    }

    if (createInvoiceSpotIds.length === 0) {
      toast.warn('Valitse vähintään yksi paikka laskulle');
      return;
    }

    try {
      setCreatingInvoice(true);

      const response = await apiRef.current.post(
        '/admin/createInvoiceForSpots',
        JSON.stringify({
          activityID: ACTIVITY_ID,
          activityId: ACTIVITY_ID,
          companyID: createInvoiceTarget.companyID,
          dueDate: formatDateForApi(createInvoiceDueDate),
          spotIDs: createInvoiceSpotIds,
          sendInvoiceByEmail: createInvoiceSendEmail,
        }),
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );

      const delivery = response?.data?.delivery;
      const bankReference = response?.data?.bankReference;

      if (createInvoiceSendEmail) {
        if (delivery === 'OK') {
          toast.success(`Lasku ${bankReference ? `#${bankReference} ` : ''}luotu ja lähetetty onnistuneesti`);
        } else {
          toast.warn(`Lasku ${bankReference ? `#${bankReference} ` : ''}luotu, mutta lähetys epäonnistui`);
        }
      } else {
        toast.success(`Lasku ${bankReference ? `#${bankReference} ` : ''}luotu onnistuneesti`);
      }

      closeCreateInvoiceModal();
      await loadReservations(false);
      await loadInvoiceMetadata();
    } catch (err) {
      console.error('createInvoiceForSpots failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Laskun luonti epäonnistui';
      toast.error(errorMessage);
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleCreateReservation = async () => {
    if (selectedSpots.length === 0) {
      toast.warn('Valitse vähintään yksi paikka');
      return;
    }

    const hasInvalidFixedPrice = selectedSpots.some((spot) => {
      const rawFixedPrice = String(spot?.fixedPrice ?? '').trim();
      return rawFixedPrice !== '' && parseMoney(rawFixedPrice) === null;
    });

    if (hasInvalidFixedPrice) {
      toast.warn('Anna fixedPrice numerona jokaiselle muokatulle paikalle');
      return;
    }

    if (companyMode === 'existing' && !selectedCompany) {
      toast.warn('Valitse yritys listalta');
      return;
    }

    if (companyMode === 'new') {
      if (!newCompany.name.trim()) {
        toast.warn('Anna yrityksen nimi');
        return;
      }

      if (!isValidEmail(newCompany.invoicingEmail)) {
        toast.warn('Anna kelvollinen laskutussähköposti');
        return;
      }

      if (!newCompany.phone.trim()) {
        toast.warn('Anna yrityksen puhelinnumero');
        return;
      }
    }

    if (contactMode === 'existing' && !selectedExistingContact) {
      toast.warn('Valitse yhteyshenkilö tai lisää uusi');
      return;
    }

    if (contactMode === 'new') {
      if (!newContact.name.trim()) {
        toast.warn('Anna yhteyshenkilön nimi');
        return;
      }

      if (!newContact.phone.trim()) {
        toast.warn('Anna yhteyshenkilön puhelinnumero');
        return;
      }

      if (!isValidEmail(newContact.email)) {
        toast.warn('Anna kelvollinen yhteyshenkilön sähköposti');
        return;
      }
    }

    const reservationPayload = {
      activityID: ACTIVITY_ID,
      activityId: ACTIVITY_ID,
      companyID: companyMode === 'new' ? 'uusi' : selectedCompany?.companyID,
      userID: contactMode === 'new' ? 'uusi' : selectedExistingContact?.userID,
      company: companyMode === 'new'
        ? { companyID: 'uusi', ...newCompany }
        : {
            companyID: selectedCompany?.companyID,
            name: selectedCompany?.company,
            invoicingEmail: selectedCompany?.invoicingEmail,
            phone: selectedCompany?.phone,
          },
      contact: contactMode === 'new'
        ? { userID: 'uusi', ...newContact }
        : {
            ...selectedExistingContact,
            userID: selectedExistingContact?.userID,
          },
      spots: selectedSpots.map((spot) => ({
        spotID: spot?.spotID,
        spotName: spot?.spotName ?? spot?.name,
        electricityType: spot?.electricityType ?? 'Ei sähköä',
        size: spot?.size,
        price: getSpotPrice(spot),
        fixedPrice: (() => {
          const parsedFixedPrice = parseMoney(spot?.fixedPrice);
          return parsedFixedPrice === null ? null : parsedFixedPrice;
        })(),
        prize: spot?.prize ?? null,
      })),
      sendInvoiceByEmail,
    };

    try {
      setSavingReservation(true);

      const response = await apiRef.current.post(
        '/admin/makeReservationForCompany',
        JSON.stringify(reservationPayload),
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );

      const invoiceDelivery = response?.data?.invoiceDelivery;
      if (sendInvoiceByEmail) {
        if (invoiceDelivery === 'OK') {
          toast.success('Varaus lisätty ja lasku lähetetty onnistuneesti');
        } else {
          toast.warn('Varaus lisätty, mutta laskun lähetys epäonnistui');
        }
      } else {
        toast.success('Varaus lisätty onnistuneesti');
      }
      closeAddReservationModal();
      await loadReservations(false);
    } catch (err) {
      console.error('makeReservationForCompany failed', err);
      const errorMessage = err?.response?.data?.msg ?? err?.response?.data?.message ?? 'Varauksen luonti epäonnistui';
      toast.error(errorMessage);
    } finally {
      setSavingReservation(false);
    }
  };

  if (!userIsAdmin) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-center text-slate-700 shadow-sm">
          Tämä sivu on tarkoitettu vain ylläpidolle.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 text-slate-900">
      <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-blue-700">Hallinta</p>
            <h1 className="mt-2 text-3xl font-black text-slate-900">Varaukset ja Laskut</h1>
            <p className="mt-2 text-sm text-slate-600">
              Tapahtuman varaukset ja laskut ryhmiteltynä yrityksittäin.
            </p>
          </div>

          <button
            type="button"
            onClick={openAddReservationModal}
            className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Lisää
          </button>
        </div>

        {!loading && statusSummary.length ? (
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {statusSummary.map((item) => (
              <div key={item.status} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{item.status}</div>
                <div className="mt-2 text-sm text-slate-700">Kpl: <span className="font-semibold text-slate-900">{item.count}</span></div>
                <div className="mt-1 text-sm text-slate-700">Yhteensä: <span className="font-semibold text-slate-900">{formatMoney(item.total)}</span></div>
              </div>
            ))}
          </div>
        ) : null}

        {!loading && companies.length ? (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
              <div>
                <label htmlFor="reservation-search" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                  Hae varauksista ja laskuista
                </label>
                <input
                  id="reservation-search"
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Hae nimellä, paikalla, viitteellä tai statuksella"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                />
              </div>

              <div>
                <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Suodata laskun statuksella</div>
                <div className="flex flex-wrap gap-2">
                  {statusSummary.map((item) => {
                    const active = selectedStatuses.includes(item.status);

                    return (
                      <label
                        key={`filter-${item.status}`}
                        className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm transition ${active ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-slate-300 bg-white text-slate-700'}`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleStatus(item.status)}
                          className="h-4 w-4"
                        />
                        <span>{item.status}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {selectedStatuses.length || searchTerm ? (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStatuses([]);
                    setSearchTerm('');
                  }}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-400 hover:text-blue-800"
                >
                  Tyhjennä suodattimet
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center text-slate-600 shadow-sm">
          Ladataan varauksia...
        </div>
      ) : companies.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center text-slate-600 shadow-sm">
          Ei varauksia tai laskuja näytettäväksi.
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center text-slate-600 shadow-sm">
          Hakua vastaavia varauksia tai laskuja ei löytynyt.
        </div>
      ) : (
        <div className="space-y-6">
          {filteredCompanies.map((company) => (
            <section
              key={`${company?.companyID ?? company?.company}`}
              className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
            >
              <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">{formatValue(company?.company, 'Tuntematon yritys')}</h2>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {company.statuses.length ? company.statuses.map((status) => (
                        <span
                          key={`${company?.companyID ?? company?.company}-${status}`}
                          className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700"
                        >
                          {status}
                        </span>
                      )) : (
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                          Ei statusta
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white">
                    Varauksia {company?.spots?.length ?? 0} · {formatMoney(company.totalAmount)}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 p-4 md:p-5 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Yrityksen tiedot</h3>
                    <button
                      type="button"
                      onClick={() => openEditCompanyModal(company)}
                      className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                    >
                      Muokkaa
                    </button>
                  </div>
                  <div className="space-y-2 text-sm text-slate-700">
                    <div className="flex items-start gap-2">
                      <FaEnvelope className="mt-0.5 text-slate-400" />
                      <span>Laskutus: {formatValue(company?.invoicingEmail)}</span>
                    </div>
                    {company?.publicEmail ? (
                      <div className="flex items-start gap-2">
                        <FaEnvelope className="mt-0.5 text-slate-400" />
                        <span>Julkinen sähköposti: {company.publicEmail}</span>
                      </div>
                    ) : null}
                    <div className="flex items-start gap-2">
                      <FaPhone className="mt-0.5 text-slate-400" />
                      <span>Puhelin: {formatValue(company?.phone)}</span>
                    </div>
                    {company?.publicPhone ? (
                      <div className="flex items-start gap-2">
                        <FaPhone className="mt-0.5 text-slate-400" />
                        <span>Julkinen puhelin: {company.publicPhone}</span>
                      </div>
                    ) : null}
                    <div className="flex items-start gap-2">
                      <FaMapMarkerAlt className="mt-0.5 text-slate-400" />
                      <span>{company.addressLines.length ? company.addressLines.join(', ') : 'Ei osoitetta'}</span>
                    </div>
                    {company?.homepage ? (
                      <div className="flex items-start gap-2">
                        <FaGlobe className="mt-0.5 text-slate-400" />
                        <a href={buildLink(company.homepage)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                          {company.homepage}
                        </a>
                      </div>
                    ) : null}
                    {company?.facebook ? (
                      <div className="flex items-start gap-2">
                        <FaFacebook className="mt-0.5 text-slate-400" />
                        <a href={buildLink(company.facebook)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                          {company.facebook}
                        </a>
                      </div>
                    ) : null}
                    {company?.instagram ? (
                      <div className="flex items-start gap-2">
                        <FaInstagram className="mt-0.5 text-slate-400" />
                        <a href={buildLink(company.instagram)} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                          {company.instagram}
                        </a>
                      </div>
                    ) : null}
                    {company?.description ? (
                      <div className="rounded-xl bg-slate-50 px-3 py-2 text-slate-600">
                        {company.description}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Yhteyshenkilöt</h3>
                  {company.contacts.length ? (
                    <div className="space-y-3">
                      {company.contacts.map((contact) => {
                        const canRemoveContact = company.contacts.length > 1;

                        return (
                          <div key={`${company?.companyID ?? company?.company}-${contact?.userID ?? contact?.email ?? contact?.name}`} className="rounded-2xl bg-slate-50 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="flex items-center gap-2 font-semibold text-slate-900">
                                  <FaUser className="text-slate-400" />
                                  <span>{formatValue(contact?.name)}</span>
                                  {Number(contact?.mainUser) === 1 ? (
                                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-bold text-blue-800">Pääyhteyshenkilö</span>
                                  ) : null}
                                </div>
                                <div className="mt-2 space-y-1 text-sm text-slate-700">
                                  <div>Puhelin: {formatValue(contact?.phone)}</div>
                                  <div>Sähköposti: {formatValue(contact?.email)}</div>
                                </div>
                              </div>

                              <div className="flex flex-wrap justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => openEditContactModal(company, contact)}
                                  className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                                >
                                  Muokkaa
                                </button>
                                {canRemoveContact ? (
                                  <button
                                    type="button"
                                    onClick={() => openRemoveContactModal(company, contact)}
                                    className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 transition hover:border-red-300 hover:bg-red-100"
                                  >
                                    Poista
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600">Ei yhteyshenkilöitä.</p>
                  )}
                </div>
              </div>

              <div className="px-4 pb-4 md:px-5 md:pb-5">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Varatut paikat</h3>
                    <button
                      type="button"
                      onClick={() => openCreateInvoiceModal(company)}
                      className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                    >
                      Luo uusi lasku
                    </button>
                  </div>

                  {company.spots.length ? (
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {company.spots.map((spot) => {
                        const spotPrice = getSpotPrice(spot);
                        const electric = hasElectricity(spot?.electricityType);

                        return (
                          <div key={`${company?.companyID ?? company?.company}-${spot?.reservationID ?? spot?.spotID ?? spot?.spotName}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="text-base font-semibold text-slate-900">Paikka {formatValue(spot?.spotName)}</div>
                                <div className="text-sm text-slate-600">Koko: {formatValue(spot?.size)}</div>
                              </div>
                              <div className="text-sm font-bold text-slate-900">{formatMoney(spotPrice)}</div>
                            </div>

                            <div className="mt-3 space-y-1.5 text-sm text-slate-700">
                              <div>Status: <span className="font-medium text-slate-900">{formatValue(spot?.status)}</span></div>
                              <div>Laskuviite: <span className="font-medium text-slate-900">{formatValue(spot?.invoiceReference)}</span></div>
                              <div>Eräpäivä: <span className="font-medium text-slate-900">{formatValue(spot?.dueDate)}</span></div>
                              <div className="flex items-center gap-2">
                                {electric ? <FaBolt className="text-amber-500" /> : null}
                                <span>Sähkö: <span className="font-medium text-slate-900">{formatValue(spot?.electricityType)}</span></span>
                              </div>
                            </div>

                            <div className="mt-4 flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEditSpotModal(company, spot)}
                                className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
                              >
                                Muokkaa
                              </button>
                              <button
                                type="button"
                                onClick={() => openDeleteModal(company, spot)}
                                className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition hover:border-red-300 hover:bg-red-100"
                              >
                                Poista
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600">Ei varattuja paikkoja.</p>
                  )}
                </div>
              </div>

              <div className="px-4 pb-4 md:px-5 md:pb-5">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Laskut</h3>

                  {(company.filteredInvoices ?? company.invoices ?? []).length ? (
                    <div className="space-y-3">
                      {(company.filteredInvoices ?? company.invoices ?? []).map((invoice) => (
                        <article key={`${company?.companyID ?? company?.company}-${invoice?.invoiceReference}`} className="rounded-2xl border border-slate-200 p-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                              <div className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">Lasku</div>
                              <div className="mt-1 text-lg font-semibold text-slate-900">#{invoice?.invoiceReference}</div>
                              <div className="mt-2 text-sm text-slate-600">Eräpäivä: <span className="font-medium text-slate-800">{formatValue(invoice?.dueDate)}</span></div>
                              {invoice?.reminderSent ? (
                                <div className="mt-1 text-sm text-slate-600">Muistutus lähetetty: <span className="font-medium text-slate-800">{invoice.reminderSent}</span></div>
                              ) : null}
                            </div>

                            <div className="flex flex-col gap-2 md:items-end">
                              <div className="flex flex-wrap gap-2 md:justify-end">
                                {(invoice?.statuses ?? []).map((status) => (
                                  <span key={`${invoice?.invoiceReference}-${status}`} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                                    {status}
                                  </span>
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-2 md:justify-end">
                                <button
                                  type="button"
                                  onClick={() => openInvoice(invoice)}
                                  disabled={!hasUsableInvoiceReference(invoice?.invoiceReference)}
                                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  Avaa lasku
                                </button>
                                <button
                                  type="button"
                                  onClick={() => sendInvoice(company, invoice)}
                                  disabled={!hasUsableInvoiceReference(invoice?.invoiceReference) || sendingInvoiceKey === getInvoiceKey(company?.companyID, invoice?.invoiceReference)}
                                  className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900 transition hover:border-emerald-400 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {sendingInvoiceKey === getInvoiceKey(company?.companyID, invoice?.invoiceReference) ? 'Lähetetään...' : 'Lähetä lasku'}
                                </button>
                                {isInvoiceOverdue(invoice) && !invoice?.reminderSent && hasUsableInvoiceReference(invoice?.invoiceReference) ? (
                                  <button
                                    type="button"
                                    onClick={() => sendReminder(company, invoice)}
                                    disabled={sendingReminderKey === getInvoiceKey(company?.companyID, invoice?.invoiceReference)}
                                    className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 transition hover:border-amber-400 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {sendingReminderKey === getInvoiceKey(company?.companyID, invoice?.invoiceReference) ? 'Lähetetään...' : 'Muistutus'}
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => openEditInvoiceModal(company, invoice)}
                                  className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-800 transition hover:border-blue-300 hover:bg-blue-100"
                                >
                                  Muokkaa laskua
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                            <div className="grid grid-cols-[minmax(0,1fr)_120px] bg-slate-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-600">
                              <div>Rivitiedot</div>
                              <div className="text-right">Hinta</div>
                            </div>

                            {(invoice?.spots ?? []).map((spot) => {
                              const spotName = spot?.spotName ?? spot?.name ?? '-';
                              const size = spot?.size ?? '-';
                              const electric = hasElectricity(spot?.electricityType);
                              const basePrice = getSpotBasePrice(spot);
                              const electricityPrice = getElectricityLinePrice(spot);

                              return (
                                <React.Fragment key={`${invoice?.invoiceReference}-${spot?.reservationID ?? spot?.spotID ?? spotName}`}>
                                  <div className="grid grid-cols-[minmax(0,1fr)_120px] items-start gap-3 border-t border-slate-200 px-3 py-3 first:border-t-0">
                                    <div>
                                      <div className="font-semibold text-slate-900">Paikka {spotName}</div>
                                      <div className="text-sm text-slate-600">Koko: {size}</div>
                                    </div>
                                    <div className="text-right font-semibold text-slate-900">{formatMoney(basePrice)}</div>
                                  </div>

                                  {electric ? (
                                    <div className="grid grid-cols-[minmax(0,1fr)_120px] items-start gap-3 border-t border-slate-200 bg-amber-50/70 px-3 py-3">
                                      <div className="flex items-start gap-2 text-amber-900">
                                        <FaBolt className="mt-0.5 text-amber-500" title="Sähkö" />
                                        <div>
                                          <div className="font-semibold">Sähköpiste paikalle {spotName}</div>
                                          <div className="text-sm text-amber-800">{spot?.electricityType ?? 'Sähkö'}</div>
                                        </div>
                                      </div>
                                      <div className="text-right font-semibold text-amber-900">{formatMoney(electricityPrice)}</div>
                                    </div>
                                  ) : null}
                                </React.Fragment>
                              );
                            })}
                          </div>

                          <div className="mt-4 flex justify-end border-t border-slate-200 pt-3 text-base font-bold text-slate-900">
                            Summa: {formatMoney(invoice?.total ?? 0)}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600">Ei laskuja.</p>
                  )}
                </div>
              </div>
            </section>
          ))}
        </div>
      )}

      {isAddModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">Lisää varaus</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">Uusi myyntipaikkavaraus</h2>
                <p className="mt-2 text-sm text-slate-600">
                  Varaus tehdään yritykselle: <span className="font-semibold text-slate-900">{companyMode === 'new' ? (newCompany.name || 'Uusi yritys') : (selectedCompany?.company ?? 'Ei valittu')}</span>
                </p>
              </div>

              <button
                type="button"
                onClick={closeAddReservationModal}
                disabled={savingReservation}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
            </div>

            <div className="mt-6 grid gap-4 xl:grid-cols-2">
              <section className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Yritys</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyMode('existing');
                        setSelectedCompanyId('');
                        setContactMode('existing');
                        setSelectedContactId('');
                      }}
                      className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${companyMode === 'existing' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                    >
                      Valitse listalta
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyMode('new');
                        setSelectedCompanyId('');
                        setSelectedContactId('');
                      }}
                      className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${companyMode === 'new' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                    >
                      Lisää yritys
                    </button>
                  </div>
                </div>

                {companyMode === 'existing' ? (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label htmlFor="reservation-company" className="mb-2 block text-sm font-semibold text-slate-700">
                        Yritys
                      </label>
                      <select
                        id="reservation-company"
                        value={selectedCompanyId}
                        onChange={(event) => handleCompanySelection(event.target.value)}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                      >
                        <option value="">Valitse yritys</option>
                        {availableCompanies.map((company) => (
                          <option key={`company-option-${company.companyID}`} value={company.companyID}>
                            {company.company}
                          </option>
                        ))}
                      </select>
                    </div>

                    {loadingCompanyOptions ? (
                      <p className="text-sm text-slate-500">Ladataan aktiivisia yrityksiä...</p>
                    ) : null}

                    {!loadingCompanyOptions && !availableCompanies.length ? (
                      <p className="rounded-2xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        Aktiivisia yrityksiä ei löytynyt listalle. Voit lisätä uuden yrityksen tästä näkymästä.
                      </p>
                    ) : null}

                    {!selectedCompanyId ? (
                      <p className="rounded-2xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                        Yhtään yritystä ei ole esivalittuna. Valitse yritys listalta tai lisää uusi yritys.
                      </p>
                    ) : null}

                    {selectedCompany ? (
                      <div className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-700">
                        <div className="font-semibold text-slate-900">{selectedCompany.company}</div>
                        <div className="mt-1">Laskutus: {formatValue(selectedCompany.invoicingEmail)}</div>
                        <div>Puhelin: {formatValue(selectedCompany.phone)}</div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <input value={newCompany.name} onChange={(event) => setNewCompany((prev) => ({ ...prev, name: event.target.value }))} placeholder="Yrityksen nimi *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.invoicingEmail} onChange={(event) => setNewCompany((prev) => ({ ...prev, invoicingEmail: event.target.value }))} placeholder="Laskutussähköposti *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.phone} onChange={(event) => setNewCompany((prev) => ({ ...prev, phone: event.target.value }))} placeholder="Puhelin *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.publicEmail} onChange={(event) => setNewCompany((prev) => ({ ...prev, publicEmail: event.target.value }))} placeholder="Julkinen sähköposti" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.publicPhone} onChange={(event) => setNewCompany((prev) => ({ ...prev, publicPhone: event.target.value }))} placeholder="Julkinen puhelin" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.homepage} onChange={(event) => setNewCompany((prev) => ({ ...prev, homepage: event.target.value }))} placeholder="Kotisivu" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.address} onChange={(event) => setNewCompany((prev) => ({ ...prev, address: event.target.value }))} placeholder="Osoite" className="rounded-xl border border-slate-300 px-3 py-2 text-sm md:col-span-2" />
                    <input value={newCompany.address2} onChange={(event) => setNewCompany((prev) => ({ ...prev, address2: event.target.value }))} placeholder="Osoite 2" className="rounded-xl border border-slate-300 px-3 py-2 text-sm md:col-span-2" />
                    <input value={newCompany.postalcode} onChange={(event) => setNewCompany((prev) => ({ ...prev, postalcode: event.target.value }))} placeholder="Postinumero" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.city} onChange={(event) => setNewCompany((prev) => ({ ...prev, city: event.target.value }))} placeholder="Toimipaikka" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.facebook} onChange={(event) => setNewCompany((prev) => ({ ...prev, facebook: event.target.value }))} placeholder="Facebook" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newCompany.instagram} onChange={(event) => setNewCompany((prev) => ({ ...prev, instagram: event.target.value }))} placeholder="Instagram" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <textarea value={newCompany.description} onChange={(event) => setNewCompany((prev) => ({ ...prev, description: event.target.value }))} placeholder="Kuvaus" rows={3} className="rounded-xl border border-slate-300 px-3 py-2 text-sm md:col-span-2" />
                  </div>
                )}
              </section>

              <section className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Yhteyshenkilö</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setContactMode('existing');
                        if (companyMode === 'existing' && availableContacts.length) {
                          setSelectedContactId(getDefaultContactId(availableContacts));
                        } else {
                          setSelectedContactId('');
                        }
                      }}
                      className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${contactMode === 'existing' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                    >
                      Valitse olemassa oleva
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setContactMode('new');
                        setSelectedContactId('');
                      }}
                      className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${contactMode === 'new' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                    >
                      Lisää yhteyshenkilö
                    </button>
                  </div>
                </div>

                {companyMode === 'existing' && !selectedCompany ? (
                  <div className="mt-4 rounded-2xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    Valitse ensin yritys, niin olemassa olevat yhteyshenkilöt tulevat näkyviin.
                  </div>
                ) : contactMode === 'existing' ? (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label htmlFor="reservation-contact" className="mb-2 block text-sm font-semibold text-slate-700">
                        Yhteyshenkilö
                      </label>
                      {loadingUserOptions ? (
                        <p className="text-sm text-slate-500">Ladataan käyttäjiä...</p>
                      ) : (
                        <select
                          id="reservation-contact"
                          value={selectedContactId}
                          onChange={(event) => setSelectedContactId(event.target.value)}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                        >
                          <option value="">Valitse yhteyshenkilö</option>
                          {companyMode === 'existing' && availableContacts.length ? (
                            availableContacts.map((contact) => (
                              <option key={`contact-option-${getContactKey(contact)}`} value={getContactKey(contact)}>
                                {formatValue(contact?.name)}
                              </option>
                            ))
                          ) : (
                            allUsers.map((user) => (
                              <option key={`user-option-${user.userID}`} value={`${user.userID}`}>
                                {formatValue(user?.name)}{user?.email ? ` (${user.email})` : ''}
                              </option>
                            ))
                          )}
                        </select>
                      )}
                    </div>

                    {selectedExistingContact ? (
                      <div className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-700">
                        <div className="font-semibold text-slate-900">{formatValue(selectedExistingContact?.name)}</div>
                        <div className="mt-1">Puhelin: {formatValue(selectedExistingContact?.phone)}</div>
                        <div>Sähköposti: {formatValue(selectedExistingContact?.email)}</div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-4 grid gap-3">
                    <input value={newContact.name} onChange={(event) => setNewContact((prev) => ({ ...prev, name: event.target.value }))} placeholder="Yhteyshenkilön nimi *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newContact.phone} onChange={(event) => setNewContact((prev) => ({ ...prev, phone: event.target.value }))} placeholder="Puhelin *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                    <input value={newContact.email} onChange={(event) => setNewContact((prev) => ({ ...prev, email: event.target.value }))} placeholder="Sähköposti *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                )}              </section>
            </div>

            <section className="mt-4 rounded-2xl border border-slate-200 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Varattavat paikat</h3>
                  <p className="mt-1 text-sm text-slate-600">Valitse paikat ja määritä tarvittaessa sähkön tyyppi.</p>
                </div>

                <button
                  type="button"
                  onClick={openSpotPicker}
                  className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-800 transition hover:border-blue-300 hover:bg-blue-100"
                >
                  Lisää paikka
                </button>
              </div>

              {selectedSpots.length ? (
                <div className="mt-4 space-y-3">
                  {selectedSpots.map((spot) => {
                    const spotKey = getSpotKey(spot);
                    const fixedPriceDisplay = spot?.fixedPrice ?? '';

                    return (
                      <div key={`selected-spot-${spotKey}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_180px_auto] md:items-start">
                          <div>
                            <div className="font-semibold text-slate-900">Paikka {formatValue(spot?.spotName ?? spot?.name)}</div>
                            <div className="text-sm text-slate-600">Koko: {formatValue(spot?.size)}</div>
                            <div className="text-sm text-slate-600">Hinta: {formatMoney(getSpotPrice(spot))}</div>
                          </div>

                          <div>
                            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Sähkön tarve</label>
                            <select
                              value={spot?.electricityType ?? 'Ei sähköä'}
                              onChange={(event) => updateSelectedSpotElectricity(spotKey, event.target.value)}
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                            >
                              {ELECTRICITY_OPTIONS.map((option) => (
                                <option key={`electricity-${spotKey}-${option}`} value={option}>{option}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Fixed price</label>
                            <input
                              value={fixedPriceDisplay}
                              onChange={(event) => updateSelectedSpotFixedPrice(spotKey, event.target.value)}
                              placeholder="Esim. 120 tai 120.50"
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                            />
                          </div>

                          <div className="flex justify-end md:justify-start">
                            <button
                              type="button"
                              onClick={() => removeSelectedSpot(spotKey)}
                              className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition hover:border-red-300 hover:bg-red-100"
                            >
                              Poista
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-4 text-sm text-slate-600">Valitse ensin yritys ja lisää sitten varattavat paikat.</p>
              )}

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-semibold text-slate-900">Laskun lähetys</p>
                <p className="mt-1 text-xs text-slate-600">Lähetetäänkö lasku sähköpostilla varauksen tehneen yrityksen laskutusosoitteeseen?</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSendInvoiceByEmail(true)}
                    className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${sendInvoiceByEmail ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'}`}
                  >
                    Kyllä
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendInvoiceByEmail(false)}
                    className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${!sendInvoiceByEmail ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'}`}
                  >
                    Ei
                  </button>
                </div>
              </div>
            </section>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeAddReservationModal}
                disabled={savingReservation}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
              <button
                type="button"
                onClick={handleCreateReservation}
                disabled={savingReservation}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingReservation ? 'Luodaan varausta...' : 'Tee varaus'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {createInvoiceTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">Luo uusi lasku</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">{formatValue(createInvoiceTarget?.companyName, 'Yritys')}</h2>
                <p className="mt-2 text-sm text-slate-600">Valitse maksamattomat paikat, eräpäivä ja halutessasi lähetä lasku heti sähköpostilla.</p>
              </div>

              <button
                type="button"
                onClick={closeCreateInvoiceModal}
                disabled={creatingInvoice}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 p-4">
              <label htmlFor="create-invoice-due-date" className="mb-2 block text-sm font-semibold text-slate-700">
                Eräpäivä
              </label>
              <input
                id="create-invoice-due-date"
                type="date"
                value={createInvoiceDueDate}
                onChange={(event) => setCreateInvoiceDueDate(event.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
              />
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-900">Laskulle valittavat maksamattomat paikat</p>
              <div className="mt-3 space-y-2">
                {(createInvoiceTarget?.spots ?? []).map((spot) => {
                  const spotID = Number(spot?.spotID);
                  const checked = createInvoiceSpotIds.includes(spotID);

                  return (
                    <label key={`create-invoice-spot-${spotID}`} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2 ${checked ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCreateInvoiceSpot(spotID)}
                        className="mt-1 h-4 w-4"
                      />
                      <div className="text-sm text-slate-700">
                        <div className="font-semibold text-slate-900">Paikka {formatValue(spot?.spotName ?? spot?.name)}</div>
                        <div>Status: {formatValue(spot?.status)}</div>
                        <div>Nykyinen viite: {formatValue(spot?.invoiceReference)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-sm font-semibold text-slate-900">Laskun lähetys</p>
              <p className="mt-1 text-xs text-slate-600">Lähetetäänkö lasku sähköpostilla heti luonnin jälkeen?</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setCreateInvoiceSendEmail(true)}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${createInvoiceSendEmail ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'}`}
                >
                  Kyllä
                </button>
                <button
                  type="button"
                  onClick={() => setCreateInvoiceSendEmail(false)}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${!createInvoiceSendEmail ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'}`}
                >
                  Ei
                </button>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeCreateInvoiceModal}
                disabled={creatingInvoice}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
              <button
                type="button"
                onClick={handleCreateInvoiceForSpots}
                disabled={creatingInvoice}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {creatingInvoice ? 'Luodaan laskua...' : 'Luo lasku'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editingInvoice ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 py-6">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">Muokkaa laskua</p>
              <h2 className="mt-1 text-xl font-bold text-slate-900">#{editingInvoice?.invoiceReference}</h2>
              <p className="mt-1 text-sm text-slate-600">{editingInvoice?.companyName}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="invoice-due-date" className="mb-2 block text-sm font-semibold text-slate-700">
                  Eräpäivä
                </label>
                <input
                  id="invoice-due-date"
                  type="date"
                  value={editDueDate}
                  onChange={(event) => setEditDueDate(event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="invoice-status" className="mb-2 block text-sm font-semibold text-slate-700">
                  Status
                </label>
                <select
                  id="invoice-status"
                  value={editStatus}
                  onChange={(event) => setEditStatus(event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                >
                  {STATUS_ORDER.map((status) => (
                    <option key={`edit-${status}`} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEditInvoiceModal}
                disabled={savingInvoice}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
              <button
                type="button"
                onClick={saveInvoiceChanges}
                disabled={savingInvoice}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingInvoice ? 'Tallennetaan...' : 'Tallenna'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editCompanyTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">Muokkaa yritystä</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">{formatValue(editCompanyTarget?.company, 'Yritys')}</h2>
                <p className="mt-2 text-sm text-slate-600">Päivitä yrityksen tiedot ja tallenna muutokset.</p>
              </div>
              <button
                type="button"
                onClick={closeEditCompanyModal}
                disabled={savingCompanyEdit}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 p-4">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Perustiedot</h3>
              <div className="grid gap-3 md:grid-cols-2">
                <input value={editCompanyForm.name} onChange={(event) => handleEditCompanyFieldChange('name', event.target.value)} placeholder="Yrityksen nimi *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editCompanyForm.invoicingEmail} onChange={(event) => handleEditCompanyFieldChange('invoicingEmail', event.target.value)} placeholder="Laskutussähköposti *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editCompanyForm.phone} onChange={(event) => handleEditCompanyFieldChange('phone', event.target.value)} placeholder="Puhelin *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editCompanyForm.publicEmail} onChange={(event) => handleEditCompanyFieldChange('publicEmail', event.target.value)} placeholder="Julkinen sähköposti" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editCompanyForm.publicPhone} onChange={(event) => handleEditCompanyFieldChange('publicPhone', event.target.value)} placeholder="Julkinen puhelin" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editCompanyForm.homepage} onChange={(event) => handleEditCompanyFieldChange('homepage', event.target.value)} placeholder="Kotisivu" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 p-4">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Osoite</h3>
              <div className="grid gap-3 md:grid-cols-2">
                <input value={editCompanyForm.address} onChange={(event) => handleEditCompanyFieldChange('address', event.target.value)} placeholder="Osoite" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 md:col-span-2" />
                <input value={editCompanyForm.address2} onChange={(event) => handleEditCompanyFieldChange('address2', event.target.value)} placeholder="Osoite 2" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 md:col-span-2" />
                <input value={editCompanyForm.postalcode} onChange={(event) => handleEditCompanyFieldChange('postalcode', event.target.value)} placeholder="Postinumero" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editCompanyForm.city} onChange={(event) => handleEditCompanyFieldChange('city', event.target.value)} placeholder="Toimipaikka" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 p-4">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Some ja kuvaus</h3>
              <div className="grid gap-3 md:grid-cols-2">
                <input value={editCompanyForm.facebook} onChange={(event) => handleEditCompanyFieldChange('facebook', event.target.value)} placeholder="Facebook" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editCompanyForm.instagram} onChange={(event) => handleEditCompanyFieldChange('instagram', event.target.value)} placeholder="Instagram" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <textarea value={editCompanyForm.description} onChange={(event) => handleEditCompanyFieldChange('description', event.target.value)} placeholder="Kuvaus" rows={4} className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500 md:col-span-2" />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEditCompanyModal}
                disabled={savingCompanyEdit}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Peruuta
              </button>
              <button
                type="button"
                onClick={handleSaveCompanyEdits}
                disabled={savingCompanyEdit}
                className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingCompanyEdit ? 'Tallennetaan...' : 'Tallenna muutokset'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editContactTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">Muokkaa yhteyshenkilöä</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">{formatValue(editContactTarget?.name, 'Yhteyshenkilö')}</h2>
                <p className="mt-2 text-sm text-slate-600">Päivitä yhteyshenkilön tiedot ja tallenna muutokset.</p>
              </div>
              <button
                type="button"
                onClick={closeEditContactModal}
                disabled={savingContactEdit}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 p-4">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Yhteystiedot</h3>
              <div className="grid gap-3">
                <input value={editContactForm.name} onChange={(event) => handleEditContactFieldChange('name', event.target.value)} placeholder="Yhteyshenkilön nimi *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editContactForm.phone} onChange={(event) => handleEditContactFieldChange('phone', event.target.value)} placeholder="Puhelin *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
                <input value={editContactForm.email} onChange={(event) => handleEditContactFieldChange('email', event.target.value)} placeholder="Sähköposti *" className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500" />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEditContactModal}
                disabled={savingContactEdit}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Peruuta
              </button>
              <button
                type="button"
                onClick={handleSaveContactEdits}
                disabled={savingContactEdit}
                className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingContactEdit ? 'Tallennetaan...' : 'Tallenna muutokset'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editSpotTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">Muokkaa paikkaa</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">Paikka {formatValue(editSpotTarget?.spotName, '—')}</h2>
                <p className="mt-2 text-sm text-slate-600">Päivitä paikan tiedot ja tallenna muutokset.</p>
              </div>
              <button
                type="button"
                onClick={closeEditSpotModal}
                disabled={savingSpotEdit}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 p-4">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Paikan tiedot</h3>
              <div className="grid gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Paikka</label>
                <select
                  value={editSpotForm.newSpotID}
                  onChange={(event) => handleEditSpotFieldChange('newSpotID', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
                  disabled={loadingSpots}
                >
                  <option value={String(editSpotTarget?.spotID ?? '')}>
                    {editSpotTarget?.spotName ?? ''} (nykyinen)
                  </option>
                  {freeSpots
                    .filter((s) => String(s.spotID) !== String(editSpotTarget?.spotID))
                    .map((s) => (
                      <option key={`edit-spot-opt-${s.spotID}`} value={String(s.spotID)}>
                        {s.name ?? s.spotName}{s.size ? ` — ${s.size}` : ''}
                      </option>
                    ))}
                </select>
                {loadingSpots && (
                  <p className="mt-1 text-xs text-slate-500">Ladataan paikkoja...</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Viitenumero</label>
                <input
                  value={editSpotForm.invoiceReference}
                  onChange={(event) => handleEditSpotFieldChange('invoiceReference', event.target.value)}
                  placeholder="Viitenumero"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <select
                value={editSpotForm.electricityType}
                onChange={(event) => handleEditSpotFieldChange('electricityType', event.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
              >
                {ELECTRICITY_OPTIONS.map((option) => (
                  <option key={`edit-spot-electricity-${option}`} value={option}>{option}</option>
                ))}
              </select>
              <input
                value={editSpotForm.fixedPrice}
                onChange={(event) => handleEditSpotFieldChange('fixedPrice', event.target.value)}
                placeholder="fixedPrice (esim. 120 tai 120.50)"
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                value={editSpotForm.dueDate}
                onChange={(event) => handleEditSpotFieldChange('dueDate', event.target.value)}
                placeholder="Eräpäivä (pp.kk.vvvv tai vvvv-kk-pp)"
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
              <select
                value={editSpotForm.status}
                onChange={(event) => handleEditSpotFieldChange('status', event.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500"
              >
                {RESERVATION_STATUS_OPTIONS.map((option) => (
                  <option key={`edit-spot-status-${option}`} value={option}>{option}</option>
                ))}
              </select>
            </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEditSpotModal}
                disabled={savingSpotEdit}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Peruuta
              </button>
              <button
                type="button"
                onClick={handleSaveSpotEdits}
                disabled={savingSpotEdit}
                className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingSpotEdit ? 'Tallennetaan...' : 'Tallenna muutokset'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {removeContactTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-600">Poista yhteyshenkilö</p>
            <h2 className="mt-2 text-xl font-bold text-slate-900">Vahvista poisto</h2>
            <p className="mt-3 text-sm text-slate-700">
              Haluatko varmasti poistaa henkilön <span className="font-semibold text-slate-900">{formatValue(removeContactTarget?.name)}</span> yrityksen <span className="font-semibold text-slate-900">{formatValue(removeContactTarget?.companyName)}</span> yhteyshenkilöistä?
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeRemoveContactModal}
                disabled={removingContact}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Ei
              </button>
              <button
                type="button"
                onClick={handleRemoveContactFromCompany}
                disabled={removingContact}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {removingContact ? 'Poistetaan...' : 'Kyllä'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {spotPickerOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">Paikkavalinta</p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">Valitse vapaat paikat</h2>
                <p className="mt-2 text-sm text-slate-600">Valitse karttanäkymästä varattavat paikat ja vahvista valinta painamalla Varaa.</p>
              </div>
            </div>

            {loadingSpots ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-600">
                Ladataan vapaita paikkoja...
              </div>
            ) : freeSpots.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-600">
                Vapaita paikkoja ei löytynyt.
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <div className="mb-2">Klikkaa kartasta vapaita paikkoja. Värit näyttävät paikan nykyisen statuksen.</div>
                  <div className="flex flex-wrap gap-2 text-xs font-semibold">
                    <span className="rounded-full bg-lime-200 px-3 py-1 text-lime-900">Vapaa</span>
                    <span className="rounded-full bg-amber-200 px-3 py-1 text-amber-900">Ennakkovaraus / Lukittu</span>
                    <span className="rounded-full bg-red-200 px-3 py-1 text-red-900">Varattu / Laskutettu</span>
                    <span className="rounded-full bg-blue-200 px-3 py-1 text-blue-900">Maksettu</span>
                    <span className="rounded-full bg-cyan-200 px-3 py-1 text-cyan-900">Valittu nyt</span>
                  </div>
                </div>

                {mappableSpots.length ? (
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="relative w-full overflow-auto bg-slate-100">
                      <svg
                        viewBox={`${mapViewBox.minX} ${mapViewBox.minY} ${mapViewBox.width} ${mapViewBox.height}`}
                        className="h-auto max-h-[70vh] min-h-[320px] w-full"
                        role="img"
                        aria-label="Myyntipaikkakartta"
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <image
                          href={MAP_IMAGE_URL}
                          x={mapViewBox.minX}
                          y={mapViewBox.minY}
                          width={mapViewBox.width}
                          height={mapViewBox.height}
                          preserveAspectRatio="xMinYMin meet"
                          pointerEvents="none"
                        />

                        {mappableSpots.map((spot) => {
                          const spotKey = getSpotKey(spot);
                          const coordinates = getSpotCoordinates(spot);
                          const selected = spotPickerSelection.includes(spotKey);
                          const isFree = isFreeSpotStatus(spot?.status);
                          const spotCenter = getSpotCenter(coordinates);
                          const pointPairs = [];

                          for (let index = 0; index < coordinates.length; index += 2) {
                            pointPairs.push(`${coordinates[index]},${coordinates[index + 1]}`);
                          }

                          const visuals = getSpotStatusVisuals(spot, selected);

                          return (
                            <g key={`map-spot-${spotKey}`}>
                              <polygon
                                points={pointPairs.join(' ')}
                                fill={visuals.fill}
                                stroke={visuals.stroke}
                                strokeWidth={selected ? 1.7 : 1.1}
                                fillOpacity={visuals.opacity}
                                onClick={() => toggleSpotInPicker(spot)}
                                style={{ cursor: isFree ? 'pointer' : 'not-allowed' }}
                              >
                                <title>{`Paikka ${spot?.spotName ?? spot?.name} · ${spot?.status ?? 'Tuntematon tila'}`}</title>
                              </polygon>

                              {selected ? (
                                <text
                                  x={spotCenter.x}
                                  y={spotCenter.y}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fontSize="5"
                                  fontWeight="700"
                                  fill="#0f172a"
                                  pointerEvents="none"
                                >
                                  {spot?.spotName ?? spot?.name}
                                </text>
                              ) : null}
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                  <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-700">Valittuja {spotPickerSelection.length}</span>
                  {spotPickerSelection.length ? freeSpots
                    .filter((spot) => spotPickerSelection.includes(getSpotKey(spot)))
                    .map((spot) => (
                      <span key={`picker-selected-label-${getSpotKey(spot)}`} className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                        {spot?.spotName ?? spot?.name}
                      </span>
                    )) : <span className="text-slate-500">Ei vielä valittuja paikkoja</span>}
                </div>

                {listOnlyFreeSpots.length ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-sm font-semibold text-slate-900">Paikat ilman karttakoordinaatteja</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {listOnlyFreeSpots.map((spot) => {
                        const spotKey = getSpotKey(spot);
                        const selected = spotPickerSelection.includes(spotKey);

                        return (
                          <button
                            key={`list-only-spot-${spotKey}`}
                            type="button"
                            onClick={() => toggleSpotInPicker(spot)}
                            className={`rounded-full border px-3 py-1 text-sm transition ${selected ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'}`}
                          >
                            {spot?.spotName ?? spot?.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSpotPickerOpen(false)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400"
              >
                Sulje
              </button>
              <button
                type="button"
                onClick={confirmSpotPickerSelection}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                Varaa
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showSendInvoiceModal && sendInvoiceModalData.invoice ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 py-6">
          <div className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Lähetä lasku</p>
              <h2 className="mt-1 text-xl font-bold text-slate-900">#{sendInvoiceModalData.invoice?.invoiceReference}</h2>
              {sendInvoiceModalData.company && (
                <p className="mt-1 text-sm text-slate-600">{sendInvoiceModalData.company.company}</p>
              )}
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-800">Valitse lähetysmenetelmä:</p>

              <div className="flex gap-3">
                <button
                  onClick={() => submitSendInvoice('test')}
                  disabled={sendInvoiceLoading}
                  className="flex-1 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2.5 font-medium text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sendInvoiceLoading ? 'Lähetetään...' : '📧 Testi lasku'}
                </button>
                <button
                  onClick={() => submitSendInvoice('real')}
                  disabled={sendInvoiceLoading}
                  className="flex-1 rounded-lg border-2 border-green-300 bg-green-50 px-4 py-2.5 font-medium text-green-700 transition hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sendInvoiceLoading ? 'Lähetetään...' : '✓ Oikea lasku'}
                </button>
              </div>

              <div className="rounded-lg bg-amber-50 p-2">
                <p className="text-xs text-amber-700">
                  <strong>Testi lasku:</strong> lähetetään vain laurinmarkkinat@lcmynamaki.fi osoitteeseen.
                </p>
                <p className="mt-1 text-xs text-amber-700">
                  <strong>Oikea lasku:</strong> lähetetään asiakkaan osoitteeseen ja kopioidaan laurinmarkkinat@lcmynamaki.fi.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => !sendInvoiceLoading && setShowSendInvoiceModal(false)}
                disabled={sendInvoiceLoading}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Peruuta
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 py-6">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-600">Poista varaus</p>
            <h2 className="mt-2 text-xl font-bold text-slate-900">Vahvista poisto</h2>
            <p className="mt-3 text-sm text-slate-700">
              Haluatko varmasti poistaa varauksen paikkaan <span className="font-semibold text-slate-900">{deleteTarget.spotName}</span>?
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deletingReservation}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sulje
              </button>
              <button
                type="button"
                onClick={handleDeleteReservation}
                disabled={deletingReservation}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingReservation ? 'Poistetaan...' : 'Poista'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ManageReservations;
